import React from "react";
import {
  Shield,
  Eye,
  Database,
  Lock,
  UserCheck,
  FileText,
  Mail,
  Server,
  Cookie,
  Share2,
  Clock,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import HeroSection from "@/components/HeroSection";
import Section from "@/components/Section";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/Button";

// ============================================
// IMMAGINI
// ============================================

const HERO_IMAGE = "/images/99-castelli-header.webp";

// ============================================
// ANIMAZIONI
// ============================================

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// ============================================
// COMPONENTE
// ============================================

const PrivacyPolicy: React.FC = () => {
  const lastUpdate = "15 Gennaio 2026";

  return (
    <PageLayout>
      {/* Hero Section */}
      <HeroSection
        title="Privacy Policy"
        subtitle="Informativa sul trattamento dei dati personali"
        backgroundImage={HERO_IMAGE}
        height="medium"
      />

      {/* Intro */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-primary/5 rounded-xl p-6 border border-primary/20 mb-8"
          >
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-primary" />
              <span className="text-sm text-text-light">Ultimo aggiornamento: {lastUpdate}</span>
            </div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <p className="text-text-light leading-relaxed">
              La presente Privacy Policy descrive le modalità di raccolta, utilizzo e protezione dei dati personali 
              degli utenti che visitano il sito web <strong className="text-foreground">www.99castelli.it</strong> (di seguito "Sito") 
              e che usufruiscono dei servizi offerti da <strong className="text-foreground">ASD 99 Castelli</strong>.
            </p>
            <p className="text-text-light leading-relaxed">
              Questa informativa è resa ai sensi dell'<strong className="text-foreground">art. 13 del Regolamento (UE) 2016/679</strong> (GDPR) 
              e si applica esclusivamente al presente Sito.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Titolare del Trattamento */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-display font-bold text-text">Titolare del Trattamento</h2>
            </div>
            
            <div className="space-y-3 text-text-light">
              <p><strong className="text-foreground">Denominazione:</strong> ASD 99 Castelli</p>
              <p><strong className="text-foreground">Sede legale:</strong> San Marco di Preturo, L'Aquila (AQ), Italia</p>
              <p><strong className="text-foreground">Codice Fiscale:</strong> 93116590667</p>
              <p><strong className="text-foreground">Codice ASD:</strong> 06700181</p>
              <p><strong className="text-foreground">Email:</strong> <a href="mailto:99castelli@gmail.com" className="text-primary hover:underline">99castelli@gmail.com</a></p>
              <p><strong className="text-foreground">Telefono:</strong> <a href="tel:+393402894567" className="text-primary hover:underline">340 2894567</a></p>
              <p><strong className="text-foreground">Legale Rappresentante:</strong> Alessandro Serreli (Presidente)</p>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Dati Raccolti */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="Dati Personali Raccolti"
            subtitle="Tipologie di informazioni che trattiamo"
          />

          <div className="space-y-6">
            {/* Dati di Navigazione */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-surface rounded-xl p-6 shadow-card border border-border"
            >
              <div className="flex items-center gap-3 mb-4">
                <Server className="w-6 h-6 text-primary" />
                <h3 className="text-lg font-semibold text-text">Dati di Navigazione</h3>
              </div>
              <p className="text-text-light mb-4">
                I sistemi informatici preposti al funzionamento del Sito acquisiscono, nel corso del loro normale esercizio, 
                alcuni dati personali la cui trasmissione è implicita nell'uso dei protocolli di comunicazione Internet.
              </p>
              <ul className="list-disc pl-6 text-text-light space-y-1">
                <li>Indirizzi IP</li>
                <li>Tipo di browser utilizzato</li>
                <li>Sistema operativo</li>
                <li>Data e ora della visita</li>
                <li>Pagine visitate</li>
              </ul>
            </motion.div>

            {/* Dati Tesseramento */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-surface rounded-xl p-6 shadow-card border border-border"
            >
              <div className="flex items-center gap-3 mb-4">
                <UserCheck className="w-6 h-6 text-primary" />
                <h3 className="text-lg font-semibold text-text">Dati per il Tesseramento</h3>
              </div>
              <p className="text-text-light mb-4">
                Per l'iscrizione all'Associazione e il tesseramento al CSI, raccogliamo:
              </p>
              <ul className="list-disc pl-6 text-text-light space-y-1">
                <li>Nome e cognome</li>
                <li>Data e luogo di nascita</li>
                <li>Codice fiscale</li>
                <li>Indirizzo di residenza</li>
                <li>Recapiti telefonici e email</li>
                <li>Documento di identità</li>
                <li>Certificato medico (ove previsto)</li>
                <li>Dati bancari per il pagamento della quota associativa</li>
              </ul>
            </motion.div>

            {/* Immagini e Video */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-surface rounded-xl p-6 shadow-card border border-border"
            >
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-6 h-6 text-primary" />
                <h3 className="text-lg font-semibold text-text">Immagini e Video</h3>
              </div>
              <p className="text-text-light">
                Durante le attività dell'Associazione potrebbero essere scattate fotografie o registrati video 
                che ritraggono i partecipanti. Tali contenuti potranno essere pubblicati sul Sito e sui canali 
                social dell'Associazione (Facebook, Instagram) esclusivamente per finalità istituzionali e promozionali, 
                previo consenso dell'interessato.
              </p>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* Finalità del Trattamento */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="Finalità del Trattamento"
            subtitle="Perché trattiamo i tuoi dati"
          />

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <div className="flex items-center gap-3 mb-6">
              <Database className="w-6 h-6 text-primary" />
              <h3 className="text-lg font-semibold text-text">I tuoi dati vengono trattati per:</h3>
            </div>
            
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-green-600 font-bold text-sm">1</span>
                </div>
                <div>
                  <strong className="text-foreground">Gestione del tesseramento</strong>
                  <p className="text-text-light text-sm">Iscrizione all'Associazione e tesseramento al CSI - Centro Sportivo Italiano</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-green-600 font-bold text-sm">2</span>
                </div>
                <div>
                  <strong className="text-foreground">Organizzazione delle attività</strong>
                  <p className="text-text-light text-sm">Escursioni in bicicletta, camminate, eventi culturali e manifestazioni</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-green-600 font-bold text-sm">3</span>
                </div>
                <div>
                  <strong className="text-foreground">Copertura assicurativa</strong>
                  <p className="text-text-light text-sm">Attivazione della polizza infortuni tramite il CSI</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-green-600 font-bold text-sm">4</span>
                </div>
                <div>
                  <strong className="text-foreground">Comunicazioni istituzionali</strong>
                  <p className="text-text-light text-sm">Invio di informazioni su attività, eventi e novità dell'Associazione</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-green-600 font-bold text-sm">5</span>
                </div>
                <div>
                  <strong className="text-foreground">Adempimenti di legge</strong>
                  <p className="text-text-light text-sm">Obblighi fiscali, contabili e normativi</p>
                </div>
              </li>
            </ul>
          </motion.div>
        </div>
      </Section>

      {/* Base Giuridica */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="Base Giuridica"
            subtitle="Fondamento legale del trattamento"
          />

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <p className="text-text-light mb-6">
              Il trattamento dei dati personali si fonda sulle seguenti basi giuridiche:
            </p>
            
            <ul className="space-y-4 text-text-light">
              <li className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span><strong className="text-foreground">Esecuzione di un contratto:</strong> per la gestione dell'iscrizione e del tesseramento (Art. 6.1.b GDPR)</span>
              </li>
              <li className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span><strong className="text-foreground">Consenso:</strong> per la pubblicazione di immagini e video, e per l'invio di comunicazioni promozionali (Art. 6.1.a GDPR)</span>
              </li>
              <li className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span><strong className="text-foreground">Obbligo legale:</strong> per gli adempimenti fiscali e normativi (Art. 6.1.c GDPR)</span>
              </li>
              <li className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span><strong className="text-foreground">Legittimo interesse:</strong> per la sicurezza informatica e il miglioramento dei servizi (Art. 6.1.f GDPR)</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </Section>

      {/* Condivisione Dati */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="Condivisione dei Dati"
            subtitle="A chi comunichiamo i tuoi dati"
          />

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <div className="flex items-center gap-3 mb-6">
              <Share2 className="w-6 h-6 text-primary" />
              <h3 className="text-lg font-semibold text-text">Destinatari dei dati</h3>
            </div>
            
            <p className="text-text-light mb-4">
              I dati personali raccolti potranno essere comunicati a:
            </p>
            
            <ul className="space-y-3 text-text-light">
              <li className="flex items-start gap-3">
                <ExternalLink className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span><strong className="text-foreground">CSI - Centro Sportivo Italiano:</strong> per il tesseramento e l'attivazione della copertura assicurativa</span>
              </li>
              <li className="flex items-start gap-3">
                <ExternalLink className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span><strong className="text-foreground">Istituti bancari:</strong> per la gestione dei pagamenti</span>
              </li>
              <li className="flex items-start gap-3">
                <ExternalLink className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span><strong className="text-foreground">Autorità competenti:</strong> quando richiesto dalla legge</span>
              </li>
              <li className="flex items-start gap-3">
                <ExternalLink className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span><strong className="text-foreground">Fornitori di servizi:</strong> provider di hosting, servizi email (in qualità di responsabili del trattamento)</span>
              </li>
            </ul>
            
            <p className="text-text-light mt-6">
              I dati non saranno diffusi né trasferiti a Paesi terzi al di fuori dell'Unione Europea.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Cookie */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <div className="flex items-center gap-3 mb-6">
              <Cookie className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-display font-bold text-text">Cookie</h2>
            </div>
            
            <p className="text-text-light mb-4">
              Il Sito utilizza cookie tecnici necessari per il corretto funzionamento delle pagine. 
              Non utilizziamo cookie di profilazione per finalità pubblicitarie.
            </p>
            
            <p className="text-text-light">
              Per maggiori informazioni sui cookie di terze parti eventualmente presenti (es. social media), 
              si invita a consultare le rispettive informative privacy.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Conservazione */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-display font-bold text-text">Conservazione dei Dati</h2>
            </div>
            
            <p className="text-text-light mb-4">
              I dati personali saranno conservati per il tempo necessario al conseguimento delle finalità 
              per cui sono stati raccolti e comunque:
            </p>
            
            <ul className="list-disc pl-6 text-text-light space-y-2">
              <li><strong className="text-foreground">Dati di tesseramento:</strong> per la durata dell'iscrizione e per i successivi 10 anni per obblighi fiscali</li>
              <li><strong className="text-foreground">Dati di navigazione:</strong> per un massimo di 12 mesi</li>
              <li><strong className="text-foreground">Immagini e video:</strong> fino a revoca del consenso</li>
            </ul>
          </motion.div>
        </div>
      </Section>

      {/* Diritti */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="I Tuoi Diritti"
            subtitle="Come esercitare i diritti previsti dal GDPR"
          />

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border mb-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-6 h-6 text-primary" />
              <h3 className="text-lg font-semibold text-text">Ai sensi degli artt. 15-22 del GDPR, hai diritto a:</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: "Accesso", desc: "Conoscere quali dati trattiamo" },
                { title: "Rettifica", desc: "Correggere dati inesatti" },
                { title: "Cancellazione", desc: "Richiedere l'eliminazione dei dati" },
                { title: "Limitazione", desc: "Limitare il trattamento" },
                { title: "Portabilità", desc: "Ricevere i dati in formato strutturato" },
                { title: "Opposizione", desc: "Opporti al trattamento" },
              ].map((diritto, index) => (
                <div key={index} className="bg-muted rounded-lg p-4">
                  <h4 className="font-semibold text-text mb-1">{diritto.title}</h4>
                  <p className="text-sm text-text-light">{diritto.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-amber-50 rounded-xl p-6 border border-amber-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-amber-600" />
              <h3 className="text-lg font-semibold text-amber-800">Come esercitare i tuoi diritti</h3>
            </div>
            <p className="text-amber-700">
              Per esercitare i tuoi diritti, puoi inviare una richiesta scritta a{" "}
              <a href="mailto:99castelli@gmail.com" className="font-semibold underline">99castelli@gmail.com</a>.
              Hai inoltre diritto di proporre reclamo al <strong>Garante per la Protezione dei Dati Personali</strong>{" "}
              (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="underline">www.garanteprivacy.it</a>).
            </p>
          </motion.div>
        </div>
      </Section>

      {/* CTA */}
      <Section background="primary" className="text-center">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Hai domande sulla privacy?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Contattaci per qualsiasi chiarimento sul trattamento dei tuoi dati personali.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              onClick={() => window.location.href = "mailto:99castelli@gmail.com"}
            >
              Contattaci
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => window.location.href = "/trasparenza"}
              className="border-white text-white hover:bg-white hover:text-primary"
            >
              Vai a Trasparenza
            </Button>
          </div>
        </motion.div>
      </Section>
    </PageLayout>
  );
};

export default PrivacyPolicy;
